// frontend/src/pages/Socrates/components/TemplateManagement/UserTemplateList/index.js

import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUsers, faEye, faEdit, faTrash, 
  faCalendarAlt, faCheckCircle, faTimesCircle, 
  faCog, faExclamationTriangle, faCheck 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { useTemplate } from '../../../contexts/TemplateContext';
import LoadingSpinner from '../../common/LoadingSpinner';
import SocratesCard from '../../common/SocratesCard';
import SocratesModal from '../../common/SocratesModal';
import styles from './UserTemplateList.module.css';

const UserTemplateList = ({ templates, onTemplateSelect }) => {
  const { t } = useLanguage();
  const { 
    setSelectedTemplate, 
    openEditForm, 
    deleteTemplate 
  } = useTemplate();
  
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteSuccess, setDeleteSuccess] = useState(false);
  const [deleteError, setDeleteError] = useState(null);

  const handleTemplateClick = (template) => {
    setSelectedTemplate(template);
    onTemplateSelect(template);
  };

  const handleEditClick = (e, template) => {
    e.stopPropagation();
    openEditForm(template);
  };

  const handleDeleteClick = (e, template) => {
    e.stopPropagation();
    setTemplateToDelete(template);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (templateToDelete) {
      try {
        setIsDeleting(true); // Add a loading state
        const response = await deleteTemplate(templateToDelete.template_id);
        
        if (response.success) {
          // Show success message
          setDeleteSuccess(true);
          setTimeout(() => {
            setDeleteConfirmOpen(false);
            setTemplateToDelete(null);
            setDeleteSuccess(false);
          }, 1500);
        }
      } catch (error) {
        console.error('Failed to delete template:', error);
        setDeleteError(error.message || 'Failed to delete template');
      } finally {
        setIsDeleting(false);
      }
    }
  };

  const cancelDelete = () => {
    setDeleteConfirmOpen(false);
    setTemplateToDelete(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('not_available');
    return new Date(dateString).toLocaleDateString();
  };

  if (!templates || templates.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faUsers} className={styles.emptyIcon} />
        <h3 className={styles.emptyTitle}>{t('no_user_templates')}</h3>
        <p className={styles.emptyDescription}>
          {t('create_your_first_template_to_get_started')}
        </p>
      </div>
    );
  }

  return (
    <div className={styles.userTemplateList}>
      <div className={styles.listHeader}>
        <h2 className={styles.listTitle}>
          <FontAwesomeIcon icon={faUsers} className={styles.titleIcon} />
          {t('user_templates')}
        </h2>
        <p className={styles.listDescription}>
          {t('user_templates_description')}
        </p>
      </div>

      <div className={styles.templateGrid}>
        {templates.map((template) => (
          <SocratesCard
            key={template.template_id}
            className={styles.templateCard}
            onClick={() => handleTemplateClick(template)}
          >
            <div className={styles.cardHeader}>
              <div className={styles.cardTitle}>
                <FontAwesomeIcon icon={faUsers} className={styles.cardIcon} />
                <h3 className={styles.templateName}>{template.template_name}</h3>
              </div>
              <div className={styles.cardStatus}>
                {template.is_active === 'true' ? (
                  <FontAwesomeIcon icon={faCheckCircle} className={styles.activeIcon} />
                ) : (
                  <FontAwesomeIcon icon={faTimesCircle} className={styles.inactiveIcon} />
                )}
              </div>
            </div>

            <div className={styles.cardContent}>
              <p className={styles.templateDescription}>
                {template.template_description || t('no_description_available')}
              </p>
              
              <div className={styles.templateMeta}>
                <div className={styles.metaItem}>
                  <FontAwesomeIcon icon={faCog} className={styles.metaIcon} />
                  <span className={styles.metaLabel}>{t('parameters')}:</span>
                  <span className={styles.metaValue}>
                    {template.parameters?.length || 0}
                  </span>
                </div>
                
                <div className={styles.metaItem}>
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.metaIcon} />
                  <span className={styles.metaLabel}>{t('created')}:</span>
                  <span className={styles.metaValue}>
                    {formatDate(template.created_at)}
                  </span>
                </div>
                
                <div className={styles.metaItem}>
                  <FontAwesomeIcon icon={faCalendarAlt} className={styles.metaIcon} />
                  <span className={styles.metaLabel}>{t('updated')}:</span>
                  <span className={styles.metaValue}>
                    {formatDate(template.updated_at)}
                  </span>
                </div>
              </div>
            </div>

            <div className={styles.cardFooter}>
              <div className={styles.actionButtons}>
                <button 
                  className={styles.viewButton}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleTemplateClick(template);
                  }}
                  title={t('view_details')}
                >
                  <FontAwesomeIcon icon={faEye} />
                  <span>{t('view')}</span>
                </button>
                
                <button 
                  className={styles.editButton}
                  onClick={(e) => handleEditClick(e, template)}
                  title={t('edit_template')}
                >
                  <FontAwesomeIcon icon={faEdit} />
                  <span>{t('edit')}</span>
                </button>
                
                <button 
                  className={styles.deleteButton}
                  onClick={(e) => handleDeleteClick(e, template)}
                  title={t('delete_template')}
                >
                  <FontAwesomeIcon icon={faTrash} />
                  <span>{t('delete')}</span>
                </button>
              </div>
              
              <div className={styles.templateId}>
                <span className={styles.idLabel}>{t('id')}:</span>
                <span className={styles.idValue}>
                  {template.template_id.substring(0, 8)}...
                </span>
              </div>
            </div>
          </SocratesCard>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <SocratesModal
        isOpen={deleteConfirmOpen}
        onClose={cancelDelete}
        title={t('confirm_delete')}
        size="small"
      >
        <div className={styles.deleteConfirmation}>
          {isDeleting ? (
            <div className={styles.deleteLoading}>
              <LoadingSpinner size="medium" />
              <p>{t('deleting_template')}</p>
            </div>
          ) : deleteSuccess ? (
            <div className={styles.deleteSuccess}>
              <div className={styles.successIcon}>
                <FontAwesomeIcon icon={faCheck} />
              </div>
              <h3 className={styles.successTitle}>{t('template_deleted')}</h3>
              <p className={styles.successMessage}>{t('template_deleted_successfully')}</p>
            </div>
          ) : (
            <>
              <div className={styles.warningIcon}>
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
              <p className={styles.confirmMessage}>
                {t('delete_template_warning', { 
                  templateName: templateToDelete?.template_name 
                })}
              </p>
              {deleteError && (
                <div className={styles.deleteError}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{deleteError}</span>
                </div>
              )}
              <div className={styles.confirmActions}>
                <button 
                  className={styles.cancelButton}
                  onClick={cancelDelete}
                >
                  {t('cancel')}
                </button>
                <button 
                  className={styles.confirmDeleteButton}
                  onClick={confirmDelete}
                >
                  {t('delete')}
                </button>
              </div>
            </>
          )}
        </div>
      </SocratesModal>
    </div>
  );
};

export default UserTemplateList;